import React, { useState } from 'react'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Delete from '@mui/icons-material/Delete'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Public from '@mui/icons-material/Public'
import PublicOff from '@mui/icons-material/PublicOff'
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useGetProjectTemplateVersions,
  useDeleteProjectTemplateVersion,
  useUpdateProjectTemplateVersion,
} from '@/service-library/hooks/project-template-versions'
import {
  useGetProjectTemplates,
  useUpdateProjectTemplate,
  useCreateProjectTemplateVersion,
} from '@/service-library/hooks/project-templates'
import { ProjectTemplateVersion } from '@/types/project-templates'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import SettingsRow from '@/components/shared/settings/SettingsRow'
import SettingsSection from '@/components/shared/settings/SettingsSection'
import DeleteProjectTemplateVersionDialog from './DeleteProjectTemplateVersionDialog'

const ProjectTemplateSettings: React.FC = () => {
  const { project } = useProjectContext()
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(
    null,
  )
  const [settingCurrentVersionId, setSettingCurrentVersionId] = useState<
    string | null
  >(null)
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(
    new Set(),
  )
  const [isCreatingVersion, setIsCreatingVersion] = useState<boolean>(false)
  const deleteOverlay = useOverlay()

  const queryClient = useQueryClient()

  // Get the project template for this project
  const {
    projectTemplates,
    isPending: isLoadingTemplates,
    queryKey: projectTemplatesQueryKey,
  } = useGetProjectTemplates({
    filters: {
      project_id: project.id,
      limit: '1',
    },
  })

  const projectTemplate = projectTemplates?.[0]

  // Get all versions for this template
  const {
    projectTemplateVersions,
    isPending: isLoadingVersions,
    queryKey: projectTemplateVersionsQueryKey,
  } = useGetProjectTemplateVersions({
    filters: {
      project_template_id: projectTemplate?.id || '',
      limit: '100',
    },
    enabled: !!projectTemplate?.id,
  })

  const { updateProjectTemplate } = useUpdateProjectTemplate({
    sideEffectQueryKeys: [projectTemplatesQueryKey],
    onSuccess: () => {
      showSuccessSnackbar('Current version updated successfully')
      setSettingCurrentVersionId(null)
    },
    onError: () => {
      showErrorSnackbar('Failed to update current version. Please try again.')
      setSettingCurrentVersionId(null)
    },
  })

  const { deleteProjectTemplateVersion } = useDeleteProjectTemplateVersion({
    listQueryKey: projectTemplateVersionsQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Template version deleted successfully')
      setDeletingVersionId(null)
      deleteOverlay.close()
    },
    onError: () => {
      showErrorSnackbar('Failed to delete template version. Please try again.')
      setDeletingVersionId(null)
    },
  })

  const { updateProjectTemplateVersion } = useUpdateProjectTemplateVersion({
    sideEffectQueryKeys: [projectTemplateVersionsQueryKey],
    onSuccess: () => {
      showSuccessSnackbar('Visibility updated successfully')
    },
    onError: () => {
      showErrorSnackbar('Failed to update visibility. Please try again.')
    },
  })

  const { createProjectTemplateVersion } = useCreateProjectTemplateVersion({
    onSuccess: () => {
      showSuccessSnackbar('New Version Created')
      setIsCreatingVersion(false)

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: projectTemplateVersionsQueryKey,
        })
      }, 3000)
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to create new template version. Please try again.',
      )
      setIsCreatingVersion(false)
    },
  })

  function handleCreateNewVersion() {
    if (!projectTemplate) return
    setIsCreatingVersion(true)
    createProjectTemplateVersion({
      template_id: projectTemplate.id,
    })
  }

  function handleSetCurrentVersion(versionId: string) {
    if (!projectTemplate) return
    setSettingCurrentVersionId(versionId)
    updateProjectTemplate({
      ...projectTemplate,
      current_version_id: versionId,
    })
  }

  function handleDeleteVersion(version: ProjectTemplateVersion) {
    setDeletingVersionId(version.id)
    deleteProjectTemplateVersion(version.id)
  }

  function toggleVersionExpansion(versionId: string) {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(versionId)) {
        newSet.delete(versionId)
      } else {
        newSet.add(versionId)
      }
      return newSet
    })
  }

  const currentVersion = projectTemplateVersions?.find(
    (version) => version.id === projectTemplate?.current_version_id,
  )

  const sortedVersions = projectTemplateVersions?.sort((a, b) => {
    // Sort by version array - assuming it's [major, minor, patch]
    for (let i = 0; i < Math.max(a.version.length, b.version.length); i++) {
      const aVal = a.version[i] || 0
      const bVal = b.version[i] || 0
      if (aVal !== bVal) {
        return bVal - aVal // Descending order (newest first)
      }
    }
    return 0
  })

  const createVersionButton = (
    <Button
      variant="text"
      disabled={isCreatingVersion || isLoadingVersions}
      onClick={handleCreateNewVersion}
      startIcon={isCreatingVersion ? <CircularProgress size={16} /> : undefined}
    >
      {isCreatingVersion ? 'Creating...' : 'Create new version'}
    </Button>
  )

  return (
    <>
      <ProjectBreadcrumb label="Template" url="../template" />

      <SettingsPage
        title="Template"
        description={
          projectTemplate
            ? `The versions of the "${projectTemplate.project_display_name}" template.`
            : undefined
        }
      >
        {isLoadingTemplates && (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={200} />
          </Stack>
        )}

        {!isLoadingTemplates && !projectTemplate && (
          <Alert severity="info">
            This project is not a template. Only projects saved as templates
            have template settings.
          </Alert>
        )}

        {projectTemplate && (
          <SettingsSection title="Current version">
            <SettingsRow
              label={
                currentVersion ? (
                  `v${currentVersion.version.join('.')}`
                ) : (
                  <Skeleton width={80} />
                )
              }
              description="Used when you create a new project from this template."
            />
          </SettingsSection>
        )}

        {projectTemplate && (
          <SettingsSection
            title="Versions"
            description="Expand a version to see its template JSON."
            actions={createVersionButton}
          >
            {isLoadingVersions ? (
              <SettingsRow>
                <Stack spacing={1}>
                  <Skeleton width="40%" />
                  <Skeleton width="60%" />
                </Stack>
              </SettingsRow>
            ) : !sortedVersions?.length ? (
              <SettingsRow>
                <Typography variant="body2" color="text.secondary">
                  No template versions found.
                </Typography>
              </SettingsRow>
            ) : (
              sortedVersions.map((version) => {
                const isCurrentVersion =
                  version.id === projectTemplate.current_version_id
                const isDeleting = deletingVersionId === version.id
                const isSettingCurrent = settingCurrentVersionId === version.id
                const canDelete = sortedVersions.length > 1 && !isCurrentVersion
                const isExpanded = expandedVersions.has(version.id)
                const versionLabel = `v${version.version.join('.')}`

                return (
                  <div key={version.id}>
                    <SettingsRow
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip title="View template JSON">
                            <IconButton
                              size="small"
                              aria-label={`View template JSON for ${versionLabel}`}
                              aria-expanded={isExpanded}
                              onClick={() => toggleVersionExpansion(version.id)}
                              sx={{ ml: -1 }}
                            >
                              {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Tooltip>
                          <span>{versionLabel}</span>
                          {isCurrentVersion && (
                            <Chip
                              icon={<CheckCircle />}
                              label="Current"
                              color="primary"
                              size="small"
                            />
                          )}
                        </Stack>
                      }
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip
                          title={
                            version.is_public
                              ? 'Public - click to make private'
                              : 'Private - click to make public'
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              updateProjectTemplateVersion({
                                ...version,
                                is_public: !version.is_public,
                              })
                            }
                          >
                            {version.is_public ? (
                              <Public color="primary" />
                            ) : (
                              <PublicOff color="action" />
                            )}
                          </IconButton>
                        </Tooltip>

                        {!isCurrentVersion && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              isSettingCurrent ? (
                                <CircularProgress size={16} />
                              ) : (
                                <RadioButtonUnchecked />
                              )
                            }
                            disabled={isSettingCurrent || isDeleting}
                            onClick={() => handleSetCurrentVersion(version.id)}
                          >
                            {isSettingCurrent ? 'Setting...' : 'Set as current'}
                          </Button>
                        )}

                        <Tooltip
                          title={
                            canDelete
                              ? 'Delete this version'
                              : sortedVersions.length === 1
                              ? 'Cannot delete the only version'
                              : 'Cannot delete the current version'
                          }
                        >
                          <span>
                            <IconButton
                              color="error"
                              size="small"
                              disabled={
                                !canDelete || isDeleting || isSettingCurrent
                              }
                              onClick={() => {
                                deleteOverlay.open()
                                setDeletingVersionId(version.id)
                              }}
                            >
                              {isDeleting ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Delete />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </SettingsRow>

                    <Collapse in={isExpanded}>
                      <Box sx={{ px: 2.5, pb: 2 }}>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            p: 2,
                            maxHeight: 800,
                            overflow: 'auto',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            fontSize: '0.8125rem',
                            fontFamily:
                              'Consolas, Monaco, "Courier New", monospace',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {JSON.stringify(version.template, null, 2)}
                        </Box>
                      </Box>
                    </Collapse>
                  </div>
                )
              })
            )}
          </SettingsSection>
        )}
      </SettingsPage>

      {/* Delete Confirmation Dialog */}
      <DeleteProjectTemplateVersionDialog
        overlay={deleteOverlay}
        version={
          sortedVersions?.find((v) => v.id === deletingVersionId) || null
        }
        onDelete={() => {
          const version = sortedVersions?.find(
            (v) => v.id === deletingVersionId,
          )
          if (version) {
            handleDeleteVersion(version)
          }
        }}
        onCancel={() => {
          setDeletingVersionId(null)
          deleteOverlay.close()
        }}
      />
    </>
  )
}

export default ProjectTemplateSettings
